import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ChevronRight, CheckCircle2, Mail, Sparkles, Play,
  Building2, Wrench, HardHat,
  ShieldCheck, FileCheck2, Brain, Layers, Compass, Users
} from 'lucide-react';
import VideoPlaceholder from '../components/VideoPlaceholder';
import ImagePlaceholder from '../components/ImagePlaceholder';
import DemoTabsHome from '../components/DemoTabsHome';
import { introVideo } from '../videos';

const trustBadges = [
  { icon: Users,    label: 'Practitioner-led',         desc: 'Built and run by people who have managed complex programmes for two decades.' },
  { icon: Building2,label: 'NHS, FM Hard Services and Construction',  desc: 'Built for the realities of these three sectors — governance, evidence and lifecycle baked in.' },
  { icon: Compass,  label: 'Embedded, not advisory',   desc: 'We work alongside your team — and leave you stronger than we arrived.' },
];

const capabilities = [
  {
    icon: ShieldCheck,
    title: 'Stage-gated governance',
    desc: 'Every project moves through your defined gates. Evidence captured, approvals logged — nothing slips.',
  },
  {
    icon: FileCheck2,
    title: 'Audit-ready by default',
    desc: 'Compliance, safety, commercial and quality evidence is built in — not bolted on. Ready for the board, the regulator, or the auditor.',
  },
  {
    icon: Brain,
    title: 'AI-powered reporting',
    desc: 'Board packs and exception reports compile themselves in minutes, not days. Narrative written. Risks surfaced. Decisions ready.',
  },
  {
    icon: Layers,
    title: 'Bespoke or templated',
    desc: 'Configure Videra to your exact way of working — or start with a proven sector workspace and trial it in 14 days.',
  },
];

const V2Home = () => (
  <div data-testid="v2-home">

    {/* ============== 1. HERO — "Is this for me?" ============== */}
    <section className="relative pt-16 pb-16 md:pt-20 md:pb-20 overflow-hidden" data-testid="v2-home-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" />
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0f172a 1px, transparent 0)',
        backgroundSize: '24px 24px'
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">

          {/* LEFT — copy + CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
              Keystone Strategic Consultants · Makers of Videra
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.2rem] font-medium tracking-tight text-slate-900 leading-[1.08]">
              Consultancy that brings <span className="text-blue-600">clarity, structure and control</span> to complex delivery.
            </h1>

            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed max-w-xl">
              <strong className="text-slate-900">Keystone Strategic Consultants</strong> helps
              organisations deliver with clarity, confidence, and control. Through practical
              expertise, structured delivery frameworks, and our <strong className="text-slate-900">Videra</strong> digital
              platform, we simplify complexity, strengthen governance, and give leaders the
              visibility they need to work smarter, reduce risk, and achieve better outcomes.
            </p>

            <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed max-w-xl">
              We work across Government, Facilities Management, PFI, Tier 1 Construction,
              Soft Services, Project Management Consultancies, and Contractor environments
              — providing mapped, auditable workflows that bring consistency, assurance, and
              operational excellence to complex delivery landscapes.
            </p>

            {/* Sector chips replace the long second paragraph */}
            <div className="mt-7 flex flex-wrap gap-2">
              {['Government', 'Facilities Management', 'PFI', 'Tier 1 Construction', 'Soft Services', 'PM Consultancies', 'Contractors'].map((s) => (
                <span key={s} className="inline-flex items-center text-[11px] font-medium text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1">
                  {s}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="v2-home-cta-contact"
                className="inline-flex items-center justify-center bg-slate-900 text-white px-7 py-3.5 font-medium tracking-wide hover:bg-slate-800 transition-all group rounded-md"
              >
                <Mail className="mr-2 w-4 h-4" /> Speak to our team
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="https://demo.videra.app"
                data-testid="v2-home-cta-platform"
                className="inline-flex items-center justify-center bg-white border border-slate-300 text-slate-900 px-7 py-3.5 font-medium tracking-wide hover:border-slate-900 transition-all rounded-md group"
              >
                Try the Videra demo
                <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>

          {/* RIGHT — product hero image + demo card stack */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-6 space-y-5"
            data-testid="v2-home-hero-visual"
          >
            {/* Product hero image — layered collage */}
            <div className="relative">
              <div className="bg-slate-900 rounded-2xl p-2.5 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.4)] ring-1 ring-slate-900/5">
                <img
                  src="/images/healthcare/hc-portfolio-tracker-hero.jpg"
                  alt="Videra Portfolio Tracker — Healthcare FM portfolio with RAG, stage and budget tracking"
                  className="w-full aspect-[16/10] object-cover object-top rounded-lg"
                />
              </div>
              {/* Floating accent screenshot — bottom-left */}
              <div className="hidden md:block absolute -bottom-6 -left-6 w-44 lg:w-52 bg-white rounded-xl p-1.5 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.35)] border border-slate-200 rotate-[-2deg]">
                <img
                  src="/images/nhs/nhs-portfolio-tracker.jpg"
                  alt="Videra NHS Portfolio Tracker"
                  className="w-full aspect-[16/10] object-cover object-top rounded-md"
                />
              </div>
              {/* Floating accent screenshot — top-right */}
              <div className="hidden md:block absolute -top-5 -right-5 w-40 lg:w-48 bg-white rounded-xl p-1.5 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.35)] border border-slate-200 rotate-[3deg]">
                <img
                  src="/images/healthcare/hc-sector-dashboard.jpg"
                  alt="Videra Hard Services Sector Command Centre"
                  className="w-full aspect-[16/10] object-cover object-top rounded-md"
                />
              </div>
            </div>

            {/* Compact "Try a live demo" card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)]">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Try a live demo · free 14-day trial
                </p>
                <Link
                  to="/sectors"
                  data-testid="v2-home-hero-sectors-link"
                  className="text-[11px] text-slate-500 hover:text-slate-900 font-medium inline-flex items-center group whitespace-nowrap"
                >
                  All sectors <ChevronRight className="w-3 h-3 ml-0.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { slug: 'nhs',           icon: Building2, label: 'NHS',              iconBg: 'text-blue-600 bg-blue-50 border-blue-100',         hoverRing: 'hover:border-blue-300' },
                  { slug: 'hard-services', icon: Wrench,    label: 'FM Hard Services', iconBg: 'text-emerald-600 bg-emerald-50 border-emerald-100', hoverRing: 'hover:border-emerald-300' },
                  { slug: 'construction',  icon: HardHat,   label: 'Construction',     iconBg: 'text-orange-600 bg-orange-50 border-orange-100',    hoverRing: 'hover:border-orange-300' },
                ].map((s) => (
                  <Link
                    key={s.slug}
                    to={`/sectors/${s.slug}`}
                    data-testid={`v2-home-hero-chip-${s.slug}`}
                    className={`group flex flex-col items-start gap-2 border border-slate-200 ${s.hoverRing} rounded-xl p-3 transition-colors bg-white`}
                  >
                    <span className={`w-8 h-8 rounded-md border flex items-center justify-center ${s.iconBg}`}>
                      <s.icon className="w-4 h-4" />
                    </span>
                    <p className="text-[13px] font-semibold text-slate-900 leading-tight">{s.label}</p>
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-100 rounded-full px-1.5 py-0.5">
                      <span className="w-1 h-1 rounded-full bg-emerald-500" /> Live
                    </span>
                  </Link>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" /> No card
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" /> 60-sec sign-up
                  </span>
                </div>
                <a
                  href="https://demo.videra.app"
                  data-testid="v2-home-hero-open-demo"
                  className="inline-flex items-center text-[12px] font-semibold text-blue-600 hover:text-blue-700 group"
                >
                  Open demo <ArrowRight className="ml-0.5 w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ============== 2. WATCH — 90s intro video (highly visible) ============== */}
    <section
      className="py-16 md:py-20 bg-gradient-to-b from-white via-slate-50 to-white border-y border-slate-200"
      data-testid="v2-home-watch"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4 inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
              The 90-second introduction
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
              Before you read the rest of the site —
              <span className="text-slate-400"> watch this first.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
              Pete Fulford, our founder, introduces Keystone and walks you through Videra
              in under two minutes — the platform we built, the problems it solves, and how
              it works across NHS Capital, Healthcare FM and Tier-1 Construction.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> 90 seconds
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Plain-English
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Real product screens
              </span>
            </div>
          </div>
          <div className="lg:col-span-7">
            <VideoPlaceholder
              number={1}
              videoUrl={introVideo.videoUrl}
              posterUrl={introVideo.posterUrl}
              title="Press play when you're ready."
              lengthSec={95}
              scriptSummary="In ninety seconds, Pete Fulford introduces Keystone, explains why we built Videra, and walks through how the platform works across NHS Capital programmes, Healthcare and FM Hard Services, and Tier-1 Construction."
            />
          </div>
        </div>
      </div>
    </section>

    {/* ============== 3. WHO IS KEYSTONE — "Who are these people?" ============== */}
    <section className="py-20 md:py-28 bg-white border-y border-slate-200" data-testid="v2-home-who">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Who we are</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1]">
            Keystone fixes complex delivery.
          </h2>
          <Link
            to="/about"
            data-testid="v2-home-who-link"
            className="inline-flex items-center mt-8 text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors group"
          >
            Read our story <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="lg:col-span-7 space-y-6 text-lg text-slate-600 leading-relaxed">
          <p>
            Keystone Strategic Consultants is a specialist delivery consultancy. We&apos;ve spent
            twenty years working alongside NHS, FM Hard Services and Construction teams —
            designing governance, mapping workflows, and building the systems that keep complex
            programmes on track.
          </p>
          <p className="text-slate-900">
            We&apos;re practitioners, not pundits. We don&apos;t write a report and disappear. We
            embed alongside your team and leave you stronger than when we arrived.
          </p>

          {/* Trust badges */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8 pt-8 border-t border-slate-200">
            {trustBadges.map((b) => (
              <div key={b.label} className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <b.icon className="w-4 h-4" />
                </span>
                <div>
                  <p className="text-sm font-medium text-slate-900 leading-snug">{b.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ============== 2.25 PRODUCT GLIMPSE — Videra dashboard band ============== */}
    <section className="pt-10 md:pt-12 pb-20 md:pb-24 bg-white" data-testid="v2-home-product-glimpse">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">A glimpse of Videra</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            One operating environment for the whole portfolio.
          </h2>
        </div>
        <div className="bg-slate-900 rounded-2xl p-2 md:p-3 shadow-[0_30px_60px_-20px_rgba(15,23,42,0.35)]">
          <ImagePlaceholder
            code="IMG-002"
            ext="jpg"
            alt="Videra Healthcare — workspace dashboard"
            aspect="16 / 10"
            brief="Videra Healthcare workspace dashboard — live KPIs, open items, next gate reviews."
            spec="2400×1500 · JPG"
            rounded="rounded-lg"
            dark
          />
        </div>

        {/* Tabbed demo switcher — Healthcare · NHS · Construction */}
        <DemoTabsHome />
      </div>
    </section>

    {/* ============== 3. WHAT VIDERA DOES — "What does the software actually do?" ============== */}
    <section className="py-20 md:py-28 bg-slate-900 text-white relative overflow-hidden" data-testid="v2-home-platform">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">The Videra platform</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
            Videra — your delivery <span className="text-blue-400">operating system.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed">
            Twenty years of hard-won delivery practice, packaged as software. One place to plan,
            govern, evidence and report — across every project in your portfolio.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {capabilities.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="bg-slate-800/60 border border-slate-700 rounded-xl p-6 hover:border-blue-500/40 transition-colors"
              data-testid={`v2-home-capability-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-300 flex items-center justify-center mb-4">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-medium text-white mb-2">{c.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:items-center">
          <a
            href="https://demo.videra.app"
            data-testid="v2-home-platform-demo"
            className="inline-flex items-center justify-center bg-white text-slate-900 px-6 py-3.5 rounded-md font-medium hover:bg-slate-100 transition-colors group"
          >
            <Play className="mr-2 w-4 h-4" /> See the full platform
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <Link
            to="/platform"
            className="inline-flex items-center justify-center text-sm font-medium text-slate-300 hover:text-white transition-colors px-4 py-3.5 group"
          >
            Platform tour <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>

    {/* ============== 4. WHY WE BUILT IT — "Why should I trust it?" + intro video ============== */}
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="v2-home-why">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Why Videra exists</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-tight">
            Built by practitioners. <span className="text-slate-400">For practitioners.</span>
          </h2>
          <div className="mt-8 space-y-5 text-lg text-slate-600 leading-relaxed">
            <p>
              Videra wasn&apos;t dreamed up in a software lab. It was built from twenty years of
              running complex delivery — in hospitals, in PFI portfolios, on construction sites —
              and seeing the same governance failures, the same reporting bottlenecks, the same
              evidence gaps, time after time.
            </p>
            <p className="text-slate-900">
              Every feature in Videra exists because it solved a real problem on a real programme.
              Nothing speculative. Nothing for show.
            </p>
            <p className="text-sm text-slate-500 uppercase tracking-[0.18em] font-semibold pt-2">— Keystone Strategic Consultants</p>
          </div>
        </div>

      </div>
    </section>

    {/* ============== 5. NEXT STEPS — "How do I start?" ============== */}
    <section className="py-24 md:py-32 bg-slate-950 text-white relative overflow-hidden" data-testid="v2-home-next">
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Next steps</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
            Two ways to start.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
          {/* Speak to our team */}
          <div
            data-testid="v2-home-next-speak"
            className="bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-10 flex flex-col"
          >
            <div className="inline-flex items-center gap-2 text-blue-300 text-[11px] uppercase tracking-[0.2em] font-semibold mb-5">
              <Mail className="w-3.5 h-3.5" /> Speak to our team
            </div>
            <h3 className="text-2xl font-medium text-white mb-3">Talk through your delivery challenge.</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-grow">
              For organisations that want bespoke governance design and hands-on delivery support.
              Start with a 30-minute discovery call — no commitment.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-white text-slate-900 px-6 py-3.5 rounded-md font-medium hover:bg-slate-100 transition-colors group"
            >
              Speak to our team
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Try Videra */}
          <div
            data-testid="v2-home-next-try"
            className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 md:p-10 flex flex-col"
          >
            <div className="inline-flex items-center gap-2 text-blue-100 text-[11px] uppercase tracking-[0.2em] font-semibold mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Try Videra
            </div>
            <h3 className="text-2xl font-medium text-white mb-3">Feel the platform for yourself.</h3>
            <p className="text-sm text-blue-50 leading-relaxed mb-6 flex-grow">
              For teams that want to feel the platform first. Trial a sector workspace in 14 days.
              No card. No commitment.
            </p>
            <a
              href="https://demo.videra.app"
              className="inline-flex items-center justify-center bg-white text-blue-700 px-6 py-3.5 rounded-md font-medium hover:bg-blue-50 transition-colors group"
            >
              <Play className="mr-2 w-4 h-4" /> Try the demo
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>

        <p className="text-center mt-10 text-sm text-slate-500">
          Not sure which? <Link to="/contact" className="text-slate-300 hover:text-white underline">Speak to our team</Link> — we&apos;ll point you to the right path.
        </p>

        <div className="mt-16 pt-10 border-t border-slate-800 flex justify-center text-sm text-slate-400">
          <a href="mailto:hello@keystoneconsulting.uk" className="inline-flex items-center gap-2 hover:text-white transition-colors">
            <Mail className="w-4 h-4" /> hello@keystoneconsulting.uk
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default V2Home;
